import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { useTRPC } from '@langmail/shared-ui'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/hooks/ui/use-toast'

interface DisconnectAccountDialogProps {
  accountId: string
  email: string
  onOpenChange: (open: boolean) => void
  open: boolean
}

/**
 * Confirms a non-primary Gmail disconnect. Calls `accounts.disconnect` which
 * revokes the Google grant and deletes the row.
 */
export function DisconnectAccountDialog({ accountId, email, open, onOpenChange }: DisconnectAccountDialogProps) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [inlineError, setInlineError] = useState<string | null>(null)

  const disconnect = useMutation({
    ...trpc.accounts.disconnect.mutationOptions(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: trpc.accounts.list.queryKey() })
      toast({ title: `Disconnected ${email}` })
      onOpenChange(false)
    },
    onError: (error) => {
      setInlineError(error.message ?? 'Failed to disconnect account')
    },
  })

  const handleConfirm = () => {
    setInlineError(null)
    disconnect.mutate({ accountId })
  }

  return (
    <AlertDialog
      onOpenChange={(next) => {
        if (!next) {
          setInlineError(null)
        }
        onOpenChange(next)
      }}
      open={open}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Disconnect {email}?</AlertDialogTitle>
          <AlertDialogDescription>
            All synced emails and labels for this account will be permanently deleted. Google access will be revoked.
            You can reconnect this Gmail later if you change your mind.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {inlineError ? <p className="text-destructive text-sm">{inlineError}</p> : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={disconnect.isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            disabled={disconnect.isPending}
            onClick={(event) => {
              event.preventDefault()
              handleConfirm()
            }}
          >
            {disconnect.isPending ? 'Disconnecting…' : 'Disconnect'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
